import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { PencilIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, Popover, Skeleton, tokens } from '@treams/treams-ui';

import Group from '../Group';
import { GroupSelection, SelectableGroupFilter } from './GroupSelector.types';
import { GroupSelectorNewModal } from './GroupSelectorNewModal';

const AddButton = styled(Box)<{ enabled?: boolean }>`
	background: ${tokens.border.subtle};
	width: 32px;
	height: 32px;
	padding: 4px;
	border-radius: 16px;

	svg {
		display: block;
		width: 24px;
		height: 24px;
	}

	${(props) =>
		props.enabled !== false &&
		`
		cursor: pointer;

		&:hover {
			background: ${props.theme.colors.lightBlueDark};

			&:active {
				background: ${tokens.text.secondary};

				svg {
					path {
						fill: ${props.theme.colors.white};
					}
				}
			}
		}`}
`;

export function GroupSelectorNew(props: {
	disabled?: boolean;
	hasExplicitSelection: boolean;
	groupSelection?: GroupSelection[];
	groupsInSelection: graphql.Group[];
	allGroups: graphql.Group[];
	canSelect: SelectableGroupFilter;
	onChange: (selection: GroupSelection[]) => void;
	buttonLabel?: string;
}) {
	const { allGroups } = props;

	const { t } = useTranslation();
	const [modalVisible, setModalVisible] = useState(false);

	const onSelect = (group: graphql.Group, recursive: boolean) => {
		if (props.hasExplicitSelection) {
			props.onChange(
				(props.groupSelection || [])
					.filter((match) => match.group.id !== group.id)
					.concat([{ group, recursive }]),
			);
		} else {
			props.onChange([{ group, recursive }]);
		}
	};

	const onDeselect = (group: graphql.Group) => {
		const newSelection = (props.groupSelection || []).filter(
			(match) => match.group.id !== group.id,
		);
		props.onChange(newSelection);
	};

	if (!props.allGroups) {
		return <div />;
	}

	return (
		<Flex alignItems="center" flexWrap="wrap" style={{ gap: tokens.gap.list }}>
			{props.hasExplicitSelection &&
				(props.groupSelection || []).map(({ group, recursive }) => (
					<Group
						key={group.id}
						query=""
						group={group}
						hasChildren={false}
						recursive={recursive}
						single={false}
						canSelect={props.canSelect(group)}
						onSelect={(recursive) => {
							onSelect(group, recursive);
						}}
						onDeselect={() => {
							onDeselect(group);
						}}
					/>
				))}
			{!props.disabled && (
				<Popover.Target
					center
					visible={modalVisible}
					onDismiss={() => setModalVisible(false)}
					contents={
						<GroupSelectorNewModal
							onClose={() => setModalVisible(false)}
							allGroups={allGroups}
							hasExplicitSelection={props.hasExplicitSelection}
							groupSelection={props.groupSelection || []}
							groupsInSelection={props.groupsInSelection}
							canSelect={props.canSelect}
							onSelect={onSelect}
							onDeselect={onDeselect}
							single={false}
						/>
					}
				>
					<Button
						onClick={() => setModalVisible(true)}
						type="secondary"
						title={t(props.buttonLabel || 'groups.selector.change.button')}
						flexShrink={0}
						flexWrap="nowrap"
					/>
				</Popover.Target>
			)}
		</Flex>
	);
}

GroupSelectorNew.Skeleton = function GroupSelectorNewSkeleton(props: {
	single?: boolean;
	required?: boolean;
	disabled?: boolean;
	buttonLabel?: string;
}) {
	const { t } = useTranslation();

	return (
		<>
			<Flex alignItems="center" style={{ gap: tokens.gap.list }}>
				<Skeleton.Repeat count={props.single ? 1 : 3}>
					<Group.Skeleton />
				</Skeleton.Repeat>
			</Flex>
			<Flex>
				{!props.disabled && (
					<>
						{props.single && props.required ? (
							<AddButton enabled={false}>
								<PencilIcon />
							</AddButton>
						) : (
							<Button
								type="secondary"
								title={t(props.buttonLabel || 'groups.selector.change.button')}
								flexShrink={0}
								flexWrap="nowrap"
								disabled
							/>
						)}
					</>
				)}
			</Flex>
		</>
	);
};
